import React from 'react';
import { Clock, Globe } from 'lucide-react';
import { 
  getTimezoneLabel, 
  getTimezoneShortLabel, 
  getRelativeOffset,
  formatOffset,
  getTimezoneOffset 
} from '../../lib/timezone';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';

// Compact timezone badge for headers
export function TimezoneBadge({ timezone, className = '' }) {
  const offset = getTimezoneOffset(timezone);
  const relativeOffset = getRelativeOffset(timezone);
  const shortLabel = getTimezoneShortLabel(timezone);
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div 
            className={`inline-flex items-center gap-1.5 px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full text-xs font-medium cursor-help ${className}`}
            data-testid="timezone-badge"
          >
            <Globe className="w-3 h-3" />
            <span>{shortLabel}</span>
          </div>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-xs">
          <div className="space-y-1">
            <p className="font-medium">{getTimezoneLabel(timezone)}</p>
            <p className="text-xs text-muted-foreground">UTC{formatOffset(offset)}</p>
            <p className="text-xs text-blue-600 dark:text-blue-400">{relativeOffset}</p>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// Full timezone display with clock icon
export function TimezoneDisplay({ timezone, showRelative = true, className = '' }) {
  const offset = getTimezoneOffset(timezone);
  const relativeOffset = getRelativeOffset(timezone);
  
  return (
    <div className={`flex items-center gap-2 text-sm ${className}`} data-testid="timezone-display">
      <Clock className="w-4 h-4 text-muted-foreground" />
      <div className="flex flex-col">
        <span className="font-medium">{getTimezoneLabel(timezone)}</span>
        <span className="text-xs text-muted-foreground">
          UTC{formatOffset(offset)}
          {showRelative && (
            <span className="ml-2 text-blue-600 dark:text-blue-400">
              ({relativeOffset})
            </span>
          )}
        </span>
      </div>
    </div>
  );
}

// Inline timezone info for timestamps
export function TimezoneTime({ time, timezone, showDate = true, className = '' }) {
  if (!time) return <span className="text-muted-foreground">-</span>;
  
  try {
    const date = new Date(time);
    const options = {
      timeZone: timezone || 'UTC',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      ...(showDate && { month: 'short', day: 'numeric' })
    };
    
    const formatted = new Intl.DateTimeFormat('en-GB', options).format(date);
    const shortTz = getTimezoneShortLabel(timezone);
    
    return (
      <span className={`inline-flex items-center gap-1 ${className}`} data-testid="timezone-time">
        <span>{formatted}</span>
        <span className="text-xs text-muted-foreground">({shortTz})</span>
      </span>
    );
  } catch (e) {
    return <span>{time}</span>;
  }
}

export default { TimezoneBadge, TimezoneDisplay, TimezoneTime };
